import type { DisciplineSlug, ChallengeType } from "@jumpbean/shared";

export interface MultipleChoiceOption<T = string | number> {
  value: T;
  label?: string;
  imageUrl?: string;
}

export interface MultipleChoiceContent<T = string | number> {
  /** Question/prompt to display */
  question?: string;
  /** Image to show (e.g., for beginning sound challenges) */
  imageUrl?: string;
  /** Available options */
  options: T[] | MultipleChoiceOption<T>[];
  /** The correct answer */
  correctAnswer: T;
  /** Challenge type for styling hints */
  challengeType?: ChallengeType;
}

export interface MultipleChoiceChallengeProps<T = string | number> {
  /** Challenge content configuration */
  content: MultipleChoiceContent<T>;
  /** Discipline for theming */
  discipline?: DisciplineSlug;
  /** Called when an answer is selected */
  onAnswer: (answer: T) => void;
  /** Currently selected answer */
  selectedAnswer: T | null;
  /** Whether answer has been submitted */
  isSubmitted?: boolean;
  /** Whether the submitted answer was correct */
  isCorrect?: boolean;
}

/**
 * Multiple Choice Challenge Component
 * A versatile component for challenges where the child picks one answer.
 * Supports number recognition, letter recognition, beginning sounds, etc.
 */
export function MultipleChoiceChallenge<T extends string | number>({
  content,
  discipline = "math",
  onAnswer,
  selectedAnswer,
  isSubmitted = false,
  isCorrect = false,
}: MultipleChoiceChallengeProps<T>) {
  // Normalize options to always have value/label format
  const normalizedOptions = content.options.map((opt) => {
    if (typeof opt === "object" && "value" in opt) {
      return opt;
    }
    return { value: opt as T, label: String(opt) };
  });

  // Determine if we should show a grid (for letters/numbers) or list (for longer options)
  const isCompact = normalizedOptions.every(
    (opt) => String(opt.label || opt.value).length <= 3
  );

  return (
    <div className="flex flex-col items-center gap-8">
      {/* Question/Image Display */}
      {(content.imageUrl || content.question) && (
        <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-neutral-200 w-full max-w-md">
          {content.imageUrl && (
            <div className="flex justify-center mb-4">
              <img
                src={content.imageUrl}
                alt="Challenge image"
                className="w-40 h-40 object-contain rounded-xl"
              />
            </div>
          )}
          {content.question && (
            <p className="text-center font-display font-bold text-body-xl text-neutral-800">
              {content.question}
            </p>
          )}
        </div>
      )}

      {/* Answer Options */}
      <div
        className={`
          w-full
          ${
            isCompact
              ? "grid grid-cols-2 sm:grid-cols-4 gap-4"
              : "flex flex-col gap-3"
          }
        `}
      >
        {normalizedOptions.map((option, index) => {
          const isSelected = selectedAnswer === option.value;
          const isCorrectOption = option.value === content.correctAnswer;

          return (
            <button
              key={index}
              type="button"
              onClick={() => !isSubmitted && onAnswer(option.value)}
              disabled={isSubmitted}
              className={`
                ${isCompact ? "min-h-[80px]" : "min-h-[64px] px-6"}
                rounded-2xl
                font-display font-bold
                ${isCompact ? "text-display-md" : "text-body-xl"}
                border-4 transition-all duration-200
                flex items-center justify-center gap-3
                ${getOptionStyles(isSelected, isSubmitted, isCorrect, isCorrectOption, discipline)}
              `}
              aria-label={`Select ${option.label || option.value}`}
              aria-pressed={isSelected}
            >
              {option.imageUrl && (
                <img
                  src={option.imageUrl}
                  alt=""
                  className="w-10 h-10 object-contain"
                />
              )}
              <span>{option.label || option.value}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Get styles for answer option based on state
 */
function getOptionStyles(
  isSelected: boolean,
  isSubmitted: boolean,
  isCorrect: boolean,
  isCorrectOption: boolean,
  discipline: DisciplineSlug
): string {
  if (isSubmitted) {
    if (isCorrectOption) {
      return `
        bg-meadow-400 border-meadow-600 text-neutral-900
        shadow-[0_4px_0_0_theme(colors.meadow.700)]
        animate-celebrate
      `;
    }
    if (isSelected && !isCorrect) {
      return `
        bg-neutral-200 border-neutral-400 text-neutral-600
        opacity-75
      `;
    }
    return `
      bg-neutral-100 border-neutral-300 text-neutral-500
      opacity-50
    `;
  }

  if (isSelected) {
    return getDisciplineSelectedStyles(discipline);
  }

  return `
    bg-white border-neutral-300 text-neutral-700
    hover:border-neutral-400 hover:bg-neutral-50
    active:scale-95
    shadow-[0_4px_0_0_theme(colors.neutral.300)]
    hover:shadow-[0_2px_0_0_theme(colors.neutral.300)]
    hover:-translate-y-0.5
  `;
}

/**
 * Get selected styles based on discipline
 */
function getDisciplineSelectedStyles(discipline: DisciplineSlug): string {
  const styles: Record<DisciplineSlug, string> = {
    math: `
      bg-luna-light border-luna-dark text-neutral-900
      shadow-[0_4px_0_0_theme(colors.luna.dark)]
      -translate-y-1
    `,
    spelling: `
      bg-petal-light border-petal-dark text-neutral-900
      shadow-[0_4px_0_0_theme(colors.petal.dark)]
      -translate-y-1
    `,
    patterns: `
      bg-prism-yellow border-prism-red text-neutral-900
      shadow-[0_4px_0_0_theme(colors.prism.red.dark)]
      -translate-y-1
    `,
    typing: `
      bg-whiskers-light border-whiskers-dark text-neutral-900
      shadow-[0_4px_0_0_theme(colors.whiskers.dark)]
      -translate-y-1
    `,
    programming: `
      bg-pixel-light border-pixel-dark text-neutral-900
      shadow-[0_4px_0_0_theme(colors.pixel.dark)]
      -translate-y-1
    `,
  };
  return styles[discipline];
}

export default MultipleChoiceChallenge;
